import { useEffect, useState } from 'react';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Alert } from '@/components/UI/Alert';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { useGetTraceQuery, type TraceEvent, type TraceSource } from '@/redux/internal-services/platformLogsApi';
import { JsonBlock, LevelBadge, fmtTime } from './shared';

const SOURCE_LABEL: Record<TraceSource, string> = { audit: 'Audit', activity: 'Activity', app: 'Application' };
const SOURCE_VARIANT: Record<TraceSource, 'primary' | 'accent' | 'steel'> = {
  audit: 'primary',
  activity: 'accent',
  app: 'steel',
};

function TraceRow({ event }: { event: TraceEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="relative border-l-2 border-ui-border pb-4 pl-4 last:pb-0">
      <span className="absolute -left-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-accent" aria-hidden />
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-ui-text3">{fmtTime(event.at)}</span>
        <Badge variant={SOURCE_VARIANT[event.source]}>{SOURCE_LABEL[event.source]}</Badge>
        {event.level && <LevelBadge level={event.level} />}
        {event.tenant_id && <span className="font-mono text-xs text-ui-text3">{event.tenant_id}</span>}
      </div>
      <div className="mt-1 flex items-start gap-2">
        <p className="min-w-0 flex-1 break-all text-sm text-ui-text">{event.summary}</p>
        <Button variant="link" size="xs" className="text-accent-text" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide' : 'Details'}
        </Button>
      </div>
      {open && <div className="mt-2"><JsonBlock value={event.data} /></div>}
    </li>
  );
}

export default function TraceTab({ correlationId, onChange }: { correlationId: string; onChange: (id: string) => void }) {
  const [input, setInput] = useState(correlationId);
  const [copied, setCopied] = useState(false);
  useEffect(() => setInput(correlationId), [correlationId]);

  const { data, isFetching, isError } = useGetTraceQuery(correlationId, { skip: !correlationId });
  const trace = data?.data;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-center gap-3"
        onSubmit={(e) => { e.preventDefault(); onChange(input.trim()); }}
      >
        <input aria-label="Correlation ID" placeholder="Paste a trace / correlation ID" value={input}
          onChange={(e) => setInput(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-full font-mono sm:w-96')} />
        <Button type="submit" variant="primary" size="sm" disabled={!input.trim()}>Trace</Button>
        {correlationId && (
          <Button type="button" variant="outline" size="sm" onClick={copyLink}>
            {copied ? 'Copied' : 'Copy trace link'}
          </Button>
        )}
      </form>

      {!correlationId ? (
        <EmptyState
          title="Follow one request end to end"
          description="Paste a correlation ID, or click any trace ID in the other tabs, to see its audit, activity and application log entries on one timeline."
        />
      ) : isFetching ? (
        <LoadingState label="Loading trace..." />
      ) : isError ? (
        <Alert tone="danger">Could not load this trace.</Alert>
      ) : !trace || trace.events.length === 0 ? (
        <EmptyState title="No log entries carry this ID." />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-ui-text2">
            {(Object.keys(SOURCE_LABEL) as TraceSource[]).map((s) => (
              <Badge key={s} variant={SOURCE_VARIANT[s]}>{SOURCE_LABEL[s]}: {trace.counts[s] ?? 0}</Badge>
            ))}
          </div>
          {trace.truncated && (
            <Alert tone="warning">This trace has more rows than the configured limit; only the first ones are shown.</Alert>
          )}
          <ol className="card-surface p-4">
            {trace.events.map((e, i) => <TraceRow key={`${e.source}-${i}`} event={e} />)}
          </ol>
        </>
      )}
    </div>
  );
}
